import { useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { UserPlus, Check } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useRegisterTalent } from "../hooks/useRegisterTalent";
import { getAgeCategory } from "../utils/register-talent.utils";
import InterestStep from "../components/InterestStep";
import BasicInfoStep from "../components/BasicInfoStep";
import TalentDetailsStep from "../components/TalentDetailsStep";
import GalleryUploadStep from "../components/GalleryUploadStep";
import { useLanguage } from "../../../context/LanguageContext";

export default function RegisterTalent() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    formData, formErrors, talentDetailsErrors, currentStep, setCurrentStep,
    talentInterests, setTalentInterests,
    selectedInterests, toggleInterest,
    computedAge, isLoading, questionsLoading, error,
    specialtiesOptions, phoneExists, checkingPhone, emailExists, checkingEmail,
    questions, questionAnswers,
    profileImage,
    handleChange, handleGalleryChange, handleProfileImageChange,
    handleBlur,
    handleSocialMediaChange, handleMeasurementsChange,
    updateCategoryGroup, handleDateOfBirthChange,
    handleBasicInfoSubmit, handleTalentDetailsSubmit,
    handleAnswerQuestion, handleGallerySubmit,
  } = useRegisterTalent();
  const STEPS = useMemo(
    () => [t("auth.registerTalent.stepBasicInfo"), t("auth.registerTalent.stepTalentDetails"), t("auth.registerTalent.stepGallery")],
    [t],
  );
  if (user) return <Navigate to="/" replace />;

  const handleContinue = () => {
    if (!selectedInterests.length) return;
    if (selectedInterests[0] === "content-creation") {
      navigate("/register/content-creator");
      return;
    }
    // "modeling" and "both" both continue through this flow
    setTalentInterests(selectedInterests);
  };

  const renderStep = () => {
    if (!talentInterests)
      return (
        <InterestStep
          selectedInterests={selectedInterests}
          onToggle={toggleInterest}
          onContinue={handleContinue}
        />
      );

    if (currentStep === 1)
      return (
        <BasicInfoStep
          formData={formData}
          formErrors={formErrors}
          phoneExists={phoneExists}
          checkingPhone={checkingPhone}
          emailExists={emailExists}
          checkingEmail={checkingEmail}
          onChange={handleChange}
          onPhoneChange={(v) => handleChange({ target: { name: "phone", value: v } } as any)}
          onPhoneCountryChange={(c) => handleChange({ target: { name: "phone_country", value: c ?? "" } } as any)}
          onBlur={handleBlur}
          onSubmit={handleBasicInfoSubmit}
        />
      );

    if (currentStep === 2)
      return (
        <TalentDetailsStep
          formData={formData}
          formErrors={formErrors}
          liveErrors={talentDetailsErrors}
          computedAge={computedAge}
          getAgeCategory={getAgeCategory}
          specialtiesOptions={specialtiesOptions}
          isLoading={questionsLoading}
          questions={questions}
          questionAnswers={questionAnswers}
          onAnswer={handleAnswerQuestion}
          onChange={handleChange}
          onDateOfBirthChange={handleDateOfBirthChange}
          onSocialMediaChange={handleSocialMediaChange}
          onMeasurementsChange={handleMeasurementsChange}
          onCategoryGroupChange={updateCategoryGroup}
          onSpecialtiesChange={(ids) => handleChange({ target: { name: "specialties", value: ids } } as any)}
          onSpecialtyOptionsChange={(ids) => handleChange({ target: { name: "specialty_options", value: ids } } as any)}
          onLanguagesChange={(values) => handleChange({ target: { name: "languages", value: values } } as any)}
          onDialectsChange={(values) => handleChange({ target: { name: "dialects", value: values } } as any)}
          onCustomLanguageChange={(v) => handleChange({ target: { name: "custom_language", value: v } } as any)}
          onBack={() => setCurrentStep(1)}
          onSubmit={handleTalentDetailsSubmit}
          talentType={talentInterests?.[0]}
          onSpecializationsChange={(values) => handleChange({ target: { name: "specializations", value: values } } as any)}
        />
      );

    return (
      <GalleryUploadStep
        gallery={formData.gallery}
        profileImage={profileImage}
        isLoading={isLoading}
        onGalleryChange={handleGalleryChange}
        onProfileImageChange={handleProfileImageChange}
        onBack={() => setCurrentStep(2)}
        onSubmit={handleGallerySubmit}
      />
    );
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-white via-gray-50 to-white flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#b3d130] rounded-full mb-4 shadow-md">
              <UserPlus size={32} className="text-[#313131]" />
            </div>
            <h1 className="text-3xl font-bold text-[#313131] mb-2">{t("auth.joinArteria")}</h1>
          </div>

          {/* Stepper — only shown after interest selection */}
          {talentInterests && (
            <div className="flex items-center justify-center mb-8 gap-0">
              {STEPS.map((label, idx) => {
                const step = idx + 1;
                const done = currentStep > step;
                const active = currentStep === step;
                return (
                  <div key={step} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                        done ? "bg-[#b3d130] border-[#b3d130] text-gray-900"
                          : active ? "bg-white border-[#b3d130] text-[#b3d130]"
                          : "bg-white border-gray-300 text-gray-400"
                      }`}>
                        {done ? <Check size={16} /> : step}
                      </div>
                      <span className={`text-xs mt-1 font-medium ${active ? "text-[#b3d130]" : done ? "text-gray-600" : "text-gray-400"}`}>
                        {label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`w-16 h-0.5 mb-4 mx-1 transition-all ${currentStep > step ? "bg-[#b3d130]" : "bg-gray-200"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {renderStep()}

          <p className="mt-6 text-center text-sm text-gray-600">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-[#b3d130] font-semibold hover:underline">{t("auth.signInLink")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
